import type { ErrorEvent, StackFrame } from "@sentry/nextjs";

/**
 * Instagram's in-app browser injects a login-form logger into every page it
 * opens; on focus it calls `logLoginFormFocused`, which `postMessage`s to the
 * app and throws InvalidAccessError when the bridge refuses. FOTO-NEXTJS-172
 * (12 events from one iPhone session) is entirely that: none of our code is on
 * the stack, and nothing about the shop changes when it fails.
 *
 * Dropped only when the logger is named (in a frame or the message) AND no
 * frame is ours. Our frames come from Next's bundles (`/_next/` chunk URLs,
 * rewritten to `app:///_next/...`) or from source paths in development; the
 * injected script has no such file — it shows as the page URL or `<anonymous>`.
 */
const INSTAGRAM_LOGGER = "logLoginFormFocused";

function isOurs(frame: StackFrame): boolean {
  const filename = frame.filename ?? "";
  return (
    filename.includes("/_next/") ||
    /^(webpack:\/\/|\.\/)?(src|app)\//.test(filename)
  );
}

export function isInstagramInjectedError(event: ErrorEvent): boolean {
  const values = event.exception?.values ?? [];
  if (!values.length) return false;
  const frames = values.flatMap((v) => v.stacktrace?.frames ?? []);
  if (frames.some(isOurs)) return false;
  const named =
    frames.some((f) => f.function?.includes(INSTAGRAM_LOGGER)) ||
    values.some((v) => v.value?.includes(INSTAGRAM_LOGGER));
  return named;
}
